import React, { useState, useEffect } from 'react';
import {
  UsersIcon,
  UserPlusIcon,
  FunnelIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  UserMinusIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useUsuarios, useEstatisticasUsuarios } from '../hooks/useUsuarios';
import { useDebounced } from '../hooks/useDebounced';
import UsuariosList from '../components/usuarios/UsuariosList';
import MatrizPapeisPermissoes from '../components/usuarios/MatrizPapeisPermissoes';
import UserDialog from '../components/usuarios/UserDialog';
import { AbasSublinhado, BadgeEmBreve, inputIris, btnPrimario } from '../components/ui/iris';
import type { UsuariosFilters, Usuario } from '../types/usuarios';

type AbaUsuarios = 'usuarios' | 'papeis';

const UsuariosPage: React.FC = () => {
  const [aba, setAba] = useState<AbaUsuarios>('usuarios');
  const [filters, setFilters] = useState<UsuariosFilters>({ page: 1, pageSize: 50 });
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  // UserDialog (handoff usuarios_dialogs): diálogo único — novo + edição
  // com abas (Dados · Perfil de acesso · Segurança).
  const [dialogNovoOpen, setDialogNovoOpen] = useState(false);
  const [dialogEditUsuario, setDialogEditUsuario] = useState<Usuario | undefined>();
  const [dialogEditTab, setDialogEditTab] = useState<'dados' | 'permissoes'>('dados');

  const { data: usuariosData, isLoading, error, refetch } = useUsuarios(filters);
  const { data: stats } = useEstatisticasUsuarios();

  const handleRefresh = () => {
    refetch();
  };

  const handleApplyFilters = (newFilters: Partial<UsuariosFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
    setShowFilters(false);
  };

  // PR5e — debounce 300ms na busca. Antes cada tecla disparava query +
  // refetch, causando flicker no grid e carga desnecessária no backend.
  const debouncedSearch = useDebounced(searchTerm, 300);
  useEffect(() => {
    setFilters(prev => {
      const novo = debouncedSearch || undefined;
      if (prev.search === novo) return prev; // evita re-render no mount inicial
      return { ...prev, search: novo, page: 1 };
    });
  }, [debouncedSearch]);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    // setFilters é diferido até debouncedSearch settle (useEffect acima)
  };

  const clearFilters = () => {
    setFilters({ page: 1, pageSize: 50 });
    setSearchTerm('');
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setFilters(prev => ({ ...prev, pageSize, page: 1 }));
  };

  // Linha da tabela → UserDialog em modo edição (aba Dados).
  const handleEditarUsuario = (usuario: Usuario) => {
    setDialogEditTab('dados');
    setDialogEditUsuario(usuario);
  };

  // PR5c — fluxo pós-criação: USER recém-criado entra direto no UserDialog
  // na aba Perfil de acesso, evitando o "criou e esqueceu sem dar acesso".
  // ADMIN não precisa (tem bypass total via Role).
  const handleUserCreated = (usuario: Usuario) => {
    if (usuario.perfil === 'USER') {
      setDialogEditTab('permissoes');
      setDialogEditUsuario(usuario);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <UserMinusIcon className="h-16 w-16 text-danger mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-tx mb-2">Erro ao carregar usuários</h1>
          <p className="text-tx-2 mb-4">Não foi possível carregar os dados. Tente novamente.</p>
          <button onClick={handleRefresh} className={btnPrimario}>
            <ArrowPathIcon className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    // Mock Íris: conteúdo de Usuários limitado a 980px centralizado.
    <div className="max-w-[1280px] mx-auto space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[22px] font-bold text-tx tracking-[-0.02em]">Usuários</h1>
          <p className="text-[13.5px] text-tx-3 mt-0.5">Membros da organização, papéis e permissões de acesso.</p>
        </div>

        <div className="flex items-center gap-2">
          {aba === 'usuarios' && (
            <>
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="p-2 rounded-iris text-tx-2 hover:bg-surface-2 transition-colors disabled:opacity-50"
                title="Atualizar dados"
                aria-label="Atualizar dados"
              >
                <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-iris transition-colors ${showFilters ? 'bg-accent-soft text-accent-text' : 'text-tx-2 hover:bg-surface-2'}`}
                title="Filtros"
                aria-label="Filtros"
                aria-pressed={showFilters}
              >
                <FunnelIcon className="h-5 w-5" />
              </button>
            </>
          )}
          {/* TODO(backend): convite por email não existe — diálogo cria usuário com senha (fluxo real). */}
          <button onClick={() => setDialogNovoOpen(true)} className={btnPrimario}>
            <UserPlusIcon className="h-4 w-4" />
            Novo usuário
          </button>
        </div>
      </header>

      <AbasSublinhado<AbaUsuarios>
        abas={[
          { key: 'usuarios', label: 'Usuários', icon: <UsersIcon className="w-4 h-4" /> },
          { key: 'papeis', label: 'Papéis & permissões', icon: <Squares2X2Icon className="w-4 h-4" /> },
        ]}
        ativa={aba}
        onChange={setAba}
      />

      {aba === 'usuarios' && (
        <>
          {/* Assentos do plano + mini-KPIs reais */}
          <div className="bg-surface border border-line rounded-iris-lg p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="min-w-[220px]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-tx">Assentos do plano</span>
                {/* TODO(backend): limite de assentos/plano não existe — SISTEMA_PLANEJAMENTO.md */}
                <BadgeEmBreve />
              </div>
              <div className="mt-1.5 flex items-center gap-3">
                <span className="text-[13px] text-tx-2">{stats ? stats.usuariosAtivos : '—'} ativos de — </span>
                <div className="w-40 h-2 rounded-full bg-surface-2 overflow-hidden">
                  <div className="h-full bg-accent rounded-full" style={{ width: '0%' }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6 ml-auto">
              <MiniKpi label="Total" valor={stats?.totalUsuarios} />
              <MiniKpi label="Ativos" valor={stats?.usuariosAtivos} />
              <MiniKpi label="Admins" valor={stats?.adminCount} />
              <MiniKpi label="Logins hoje" valor={stats?.loginsHoje} />
            </div>
          </div>

          {/* Busca + filtros */}
          <div className="bg-surface border border-line rounded-iris-lg p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tx-3" />
                <input
                  type="text"
                  placeholder="Buscar por nome, login ou email..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className={`w-full pl-9 ${inputIris}`}
                />
              </div>

              {Object.keys(filters).filter(key => key !== 'page' && key !== 'pageSize').length > 0 && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-tx-2 hover:text-tx transition-colors"
                >
                  Limpar filtros
                </button>
              )}
            </div>

            {showFilters && (
              <div className="mt-4 pt-4 border-t border-line">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-tx-2 mb-1.5">Perfil</label>
                    <select
                      value={filters.perfil || ''}
                      onChange={(e) => handleApplyFilters({ perfil: e.target.value || undefined })}
                      className={`w-full ${inputIris}`}
                    >
                      <option value="">Todos os perfis</option>
                      <option value="ADMIN">Administrador</option>
                      <option value="USER">Usuário</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-tx-2 mb-1.5">Status</label>
                    <select
                      value={filters.ativo?.toString() || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleApplyFilters({ ativo: value === '' ? undefined : value === 'true' });
                      }}
                      className={`w-full ${inputIris}`}
                    >
                      <option value="">Todos os status</option>
                      <option value="true">Ativo</option>
                      <option value="false">Inativo</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-tx-2 mb-1.5">Itens por página</label>
                    <select
                      value={filters.pageSize || 50}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className={`w-full ${inputIris}`}
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <UsuariosList
            usuariosData={usuariosData}
            onEdit={handleEditarUsuario}
            isLoading={isLoading}
            currentPage={filters.page || 1}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {aba === 'papeis' && <MatrizPapeisPermissoes />}

      {/* Modais */}
      <UserDialog
        mode="new"
        isOpen={dialogNovoOpen}
        onClose={() => setDialogNovoOpen(false)}
        onUserCreated={handleUserCreated}
      />

      {dialogEditUsuario && (
        <UserDialog
          mode="edit"
          usuario={dialogEditUsuario}
          isOpen
          initialTab={dialogEditTab}
          onClose={() => setDialogEditUsuario(undefined)}
        />
      )}
    </div>
  );
};

const MiniKpi: React.FC<{ label: string; valor?: number }> = ({ label, valor }) => (
  <div className="text-right">
    <div className="text-lg font-bold text-tx leading-tight">{valor ?? '—'}</div>
    <div className="text-[11px] uppercase tracking-wide text-tx-3">{label}</div>
  </div>
);

export default UsuariosPage;
